import React, { useMemo } from "react";
import Button from "../../component/button/Button";
import { IoIosArrowDown } from "react-icons/io";
import styles from "./AnalyticsDashboard.module.css";
import useUpdateSearchParams from "../../hooks/useUpdateSearchParams";

// Local-date formatting, not toISOString(): that converts to UTC first, which
// can shift "Today"/preset boundaries by a day for users outside UTC.
const toISODate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const PRESETS = [
  { label: "Today", days: 0 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

// A preset's `days` is its label (e.g. 7 for "7d"), but the backend treats
// `from`/`to` as an inclusive calendar range, so "N days" means today plus
// the previous N-1 days -- offsetting `from` by the full N days would pull
// in one extra day (7d showing 8 days' worth of data, 30d showing 31, etc).
const presetOffsetDays = (days) => (days === 0 ? 0 : days - 1);

const CONTENT_TYPES = ["Movie", "Podcast", "Live Channel", "Short Clip"];

// Global date-range + comparison + platform/content-type filter bar. Every
// filter is synced to URL query params (extending the existing `?tab=`
// pattern already in AnalyticsDashboard.js) so filtered views stay shareable
// and survive a page reload. Sections read these same params directly via
// useSearchParams rather than receiving them as props, so there's no prop
// drilling as new sections are added.
const FilterBar = ({ platforms = [] }) => {
  const [searchParams, updateParams] = useUpdateSearchParams();

  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const compare = searchParams.get("compare") === "previous_period";
  const platform = searchParams.get("platform") || "";
  const contentType = searchParams.get("contentType") || "";

  // Recomputed every render (not hoisted to module scope) so it doesn't go
  // stale after midnight if the tab is left open.
  const todayISO = toISODate(new Date());

  // The backend already defaults an unfiltered request to a trailing 30-day
  // window (see AnalyticsService.buildAnalyticsWhere). Writing that range
  // into the URL on load to highlight "30d" used to cause every section's
  // fetch effect (which depends on from/to) to run twice on first load: once
  // for the initial empty from/to, then again once this seeded them. Since
  // "no from/to" and "30d" are the same request to the backend, just treat
  // the unfiltered state as the 30d preset for display -- no URL write, no
  // second fetch.
  // Chromium's native date picker can silently commit whatever day it's
  // focused on (e.g. after clicking the prev/next-month arrow) the moment
  // you click away, IF the field's value was empty when the picker opened --
  // there's nothing selected for it to "keep," so it grabs the focused one.
  // Falling back to the actual effective range (same default the backend
  // applies when from/to are unset, see the comment below) instead of ""
  // means the picker always opens on a real selected day, which Chromium
  // does not clobber on blur.
  const defaultRange = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - presetOffsetDays(30) * 24 * 60 * 60 * 1000);
    return { from: toISODate(fromDate), to: toISODate(toDate) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePreset = useMemo(() => {
    if (!from && !to) return "30d";
    if (!from || !to) return "";
    const offsetDays = Math.round((new Date(to) - new Date(from)) / (24 * 60 * 60 * 1000));
    const preset = PRESETS.find((p) => presetOffsetDays(p.days) === offsetDays);
    return preset ? preset.label : "";
  }, [from, to]);

  const applyPreset = (days) => {
    const toDate = new Date();
    const fromDate = new Date(toDate.getTime() - presetOffsetDays(days) * 24 * 60 * 60 * 1000);
    updateParams({ from: toISODate(fromDate), to: toISODate(toDate) });
  };

  return (
    <div className={styles.filterBar}>
      <div className={styles.filterGroup}>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className={activePreset === p.label ? styles.presetActive : styles.preset}
            onClick={() => applyPreset(p.days)}
          >
            {p.label}
          </button>
        ))}
        <input
          type="date"
          className={styles.dateInput}
          value={from || defaultRange.from}
          max={to || todayISO}
          onChange={(e) => updateParams({ from: e.target.value })}
        />
        <span className={styles.dateSep}>–</span>
        <input
          type="date"
          className={styles.dateInput}
          value={to || defaultRange.to}
          min={from || undefined}
          max={todayISO}
          onChange={(e) => updateParams({ to: e.target.value })}
        />
      </div>

      {/* <label className={styles.compareToggle}>
        <input
          type="checkbox"
          checked={compare}
          onChange={(e) => updateParams({ compare: e.target.checked ? "previous_period" : "" })}
        />
        Compare to previous period
      </label> */}

      <div className={styles.filterGroup}>
        <Button
          type="select"
          value={platform}
          onClick={(e) => updateParams({ platform: e.target.value })}
          options={platforms}
          selectLabel="All Platforms"
          variant="outline"
          iconRight={<IoIosArrowDown />}
          className={styles.filterSelect}
        />
        <Button
          type="select"
          value={contentType}
          onClick={(e) => updateParams({ contentType: e.target.value })}
          options={CONTENT_TYPES}
          selectLabel="All Content Types"
          variant="outline"
          iconRight={<IoIosArrowDown />}
          className={styles.filterSelect}
        />
      </div>
    </div>
  );
};

export default FilterBar;
